/**
 * Shared chat/constructor command parser.
 * Returns { handled, serverText, pendingCommand, localReply, metasid }
 *   handled     — true if the command was recognised
 *   serverText  — text to send to backend (null for purely local commands)
 *   pendingCommand — original command string to track the response
 *   localReply  — immediate reply text for the user (null if none)
 *   metasid     — extracted metasid for /create (null otherwise)
 */

export function parseCommand(text) {
  const cmd = text.trim().toLowerCase()

  if (cmd === '/help' || cmd === 'помощь') {
    return {
      handled: true, serverText: null, pendingCommand: null, localReply: 'help', metasid: null,
    }
  }

  if (cmd === '/clear') {
    return {
      handled: true, serverText: null, pendingCommand: null, localReply: 'clear', metasid: null,
    }
  }

  if (cmd.startsWith('/create ')) {
    const args = text.trim().substring('/create '.length).trim()
    const spaceIdx = args.indexOf(' ')
    if (spaceIdx < 0) {
      return {
        handled: true,
        serverText: null,
        pendingCommand: null,
        localReply: 'Укажите тип и имя объекта. Например: `/create table Заказы`\nДоступные типы: `table`, `catalog`, `cube`, `form`, `report`, `procedure`, `import`',
        metasid: null,
      }
    }
    const metasid = args.substring(0, spaceIdx).trim().toLowerCase()
    const rawName = args.substring(spaceIdx + 1).trim()
    if (!rawName) {
      return {
        handled: true,
        serverText: null,
        pendingCommand: null,
        localReply: `Укажите имя объекта. Например: \`/create ${metasid} Заказы\``,
        metasid: null,
      }
    }
    const objectName = rawName.split(/\s+/).map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')
    return {
      handled: true,
      serverText: `создай_объект ${metasid} ${objectName}`,
      pendingCommand: `/create ${metasid} ${objectName}`,
      localReply: null,
      metasid,
    }
  }

  if (cmd.startsWith('/obj ')) {
    const objectName = text.trim().substring(5).trim()
    if (!objectName) {
      return {
        handled: true, serverText: null, pendingCommand: null, localReply: 'Укажите имя объекта. Например: `/obj Пользователи`', metasid: null,
      }
    }
    return {
      handled: true, serverText: `открой ${objectName}`, pendingCommand: `/obj ${objectName}`, localReply: null, metasid: null,
    }
  }

  if (cmd.startsWith('/sel ')) {
    const num = text.trim().substring(5).trim()
    if (!num || !/^\d+$/.test(num)) {
      return {
        handled: true, serverText: null, pendingCommand: null, localReply: 'Укажите номер элемента. Например: `/sel 1`', metasid: null,
      }
    }
    return {
      handled: true, serverText: `выбери ${num}`, pendingCommand: `/sel ${num}`, localReply: null, metasid: null,
    }
  }

  if (cmd.startsWith('/add_attr ')) {
    const args = text.trim().substring('/add_attr '.length).trim().split(/\s+/)
    if (args.length < 2) {
      return {
        handled: true, serverText: null, pendingCommand: null, localReply: 'Использование: `/add_attr <object_id> <entity_id>`', metasid: null,
      }
    }
    return {
      handled: true, serverText: text.trim(), pendingCommand: text.trim(), localReply: null, metasid: null,
    }
  }

  if (cmd.startsWith('/add attr ')) {
    const attrName = text.trim().substring('/add attr '.length).trim()
    if (!attrName) {
      return {
        handled: true, serverText: null, pendingCommand: null, localReply: 'Укажите имя атрибута. Например: `/add attr Название`', metasid: null,
      }
    }
    return {
      handled: true, serverText: `добавь атрибут ${attrName}`, pendingCommand: `/add attr ${attrName}`, localReply: null, metasid: null,
    }
  }

  if (cmd === '/attrs') {
    return {
      handled: true, serverText: 'покажи атрибуты', pendingCommand: '/attrs', localReply: null, metasid: null,
    }
  }

  if (cmd === '/datatypes') {
    return {
      handled: true, serverText: '/datatypes', pendingCommand: '/datatypes', localReply: null, metasid: null,
    }
  }

  if (cmd === '/export') {
    return {
      handled: true, serverText: null, pendingCommand: null, localReply: 'export', metasid: null,
    }
  }

  return {
    handled: false, serverText: null, pendingCommand: null, localReply: null, metasid: null,
  }
}

export const HELP_TEXT = `**Доступные команды:**
- **/obj xxx** — найти и выбрать объект по имени
- **/create type name** — создать объект (type: table, catalog, cube, form, report, procedure, import)
- **/create join name** — создать связку (join)
- **/attrs** — показать атрибуты текущего объекта
- **/sel N** — выбрать элемент из списка по номеру
- **/add attr name** — добавить атрибут в текущий объект
- **/add_attr object_id entity_id** — добавить атрибут по ID
- **/export** — экспортировать структуру выбранных объектов в JSON
- **/datatypes** — список типов данных
- **/mcp_tools** — список MCP тулов
- **/clear** — очистить контекст
- **/help** — список команд`
